/**
 * JWT Authentication Manager
 * Cookie-based session handling — tokens live in httpOnly cookies only.
 */

import { ensureCsrfCookie, getCookie } from './global';
import { apiEndpoints } from '@config/env';
import { error as logError } from './console';
import { config } from '@config/env';

/** Skip verbose authManager traces for the logging endpoint (avoids meta-noise when debug DB logging is on). */
const isLoggingEndpointUrl = (url: string): boolean => url.includes('/api/log/message');

/** Stream service auth failures must not trigger app session refresh/clear. */
const isStreamServiceUrl = (url: string): boolean => url.includes('/api/stream/');

/** Non-fetch options for makeAuthenticatedRequest. */
export interface AuthRequestOptions {
  /**
   * The caller degrades gracefully when this request fails, so a network-level failure is not an
   * application error and must not be logged as one.
   *
   * For a background poll — one nobody asked for, whose failure is invisible because the caller keeps
   * its previous value — a dropped request says nothing about the application: the laptop slept, the
   * wifi blinked, the tab was closed mid-flight. Logging that at error level writes a row to
   * admin.log_activity for every user on every blip and buries the failures that do mean something.
   * Retries are unaffected; only the level of the final log changes.
   */
  bestEffort?: boolean;
}

interface LoginResponse {
  success: boolean;
  data?: {
    accessToken?: string;
    refreshToken?: string;
    expiresIn: number;
    user: any;
  };
  message?: string;
}

interface RegisterResponse {
  success: boolean;
  message?: string;
}

interface UserResponse {
  success: boolean;
  data?: any;
  message?: string;
}

interface RefreshResponse {
  success: boolean;
  data?: {
    accessToken?: string;
    refreshToken?: string;
    expiresIn: number;
    user?: Record<string, unknown>;
  };
  message?: string;
}

interface UserData {
  email: string;
  password: string;
  [key: string]: any;
}

/**
 * Thrown when token refresh fails for a recoverable reason (network, 5xx, etc.).
 * Callers must not clear the session — the user can retry once connectivity or the server recovers.
 */
export class TransientTokenRefreshError extends Error {
  override readonly name = 'TransientTokenRefreshError';
  constructor(message?: string) {
    super(message ?? 'Token refresh failed (transient)');
  }
}

export function isTransientTokenRefreshError(e: unknown): boolean {
  return e instanceof TransientTokenRefreshError;
}

class AuthManager {
  private sessionKey: string;
  private tokenExpiresKey: string;
  private isRefreshing: boolean;
  private refreshPromise: Promise<boolean> | null;
  private refreshTimerId: number | null;
  private activityCheckIntervalId: number | null;
  private lastActivityTime: number;
  private isPageVisible: boolean;
  private activityListenersAttached: boolean;
  private lastLiveUserSyncMs: number;
  private readonly liveUserSyncMinIntervalMs = 30_000;

  constructor() {
    this.sessionKey = 'session_active';
    this.tokenExpiresKey = 'token_expires';
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.refreshTimerId = null;
    this.activityCheckIntervalId = null;
    this.lastActivityTime = Date.now();
    this.isPageVisible = typeof document !== 'undefined' ? !document.hidden : true;
    this.activityListenersAttached = false;
    this.lastLiveUserSyncMs = 0;

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      this.initializeActivityDetection();
      this.attachCrossTabStorageSync();
    }
  }

  private async applyLiveUserToStore(userData: Record<string, unknown>): Promise<void> {
    const { setUser } = await import('../store/userStore');
    setUser(userData as import('../store/userStore').User);
    this.lastLiveUserSyncMs = Date.now();
  }

  /**
   * Refresh user permissions and profile fields from the server without re-login.
   */
  async syncLiveUserToStore(force = false): Promise<void> {
    if (!this.hasSession()) {
      return;
    }
    const now = Date.now();
    if (!force && now - this.lastLiveUserSyncMs < this.liveUserSyncMinIntervalMs) {
      return;
    }
    try {
      const result = await this.fetchCurrentUserWithCookies();
      if (result.success && result.data) {
        await this.applyLiveUserToStore(result.data as Record<string, unknown>);
      }
    } catch {
      // Non-fatal: stale permissions until next sync
    }
  }

  private clearUserStoreSession(): void {
    if (typeof window === 'undefined') return;
    void import('../store/userStore')
      .then(({ setIsLoggedIn, setUser, setSubscription }) => {
        setIsLoggedIn(false);
        setUser(null);
        setSubscription(null);
      })
      .catch(() => {});
  }

  private attachCrossTabStorageSync(): void {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== this.sessionKey && e.key !== this.tokenExpiresKey) {
        return;
      }
      if (typeof sessionStorage === 'undefined' || typeof localStorage === 'undefined') return;
      if (this.hasSession()) {
        return;
      }
      if (this.refreshTimerId) {
        clearTimeout(this.refreshTimerId);
        this.refreshTimerId = null;
      }
      this.stopActivityCheck();
      this.clearUserStoreSession();
    });
  }

  markSessionFromAuth(expiresIn: number): void {
    this.storeSessionMetadata(expiresIn);
  }

  /** @deprecated Tokens are httpOnly cookies; always returns null. */
  getAccessToken(): string | null {
    return null;
  }

  /** @deprecated Tokens are httpOnly cookies; always returns null. */
  getRefreshToken(): string | null {
    return null;
  }

  hasSession(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(this.sessionKey) === 'true';
  }

  isTokenExpired(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    const expires = sessionStorage.getItem(this.tokenExpiresKey);
    if (!expires) {
      // Metadata can be missing while httpOnly auth cookies are still valid.
      return false;
    }
    const expiresMs = parseInt(expires, 10);
    if (!Number.isFinite(expiresMs)) {
      return false;
    }
    const skewMs = 60 * 1000;
    return Date.now() + skewMs > expiresMs;
  }

  private isTokenNearExpiry(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    const expires = sessionStorage.getItem(this.tokenExpiresKey);
    if (!expires) {
      return false;
    }
    const expiresMs = parseInt(expires, 10);
    if (!Number.isFinite(expiresMs)) {
      return false;
    }
    const leewayMs = 5 * 60 * 1000;
    return Date.now() + leewayMs > expiresMs;
  }

  private storeSessionMetadata(expiresIn: number): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(this.sessionKey, 'true');
      sessionStorage.setItem(this.tokenExpiresKey, (Date.now() + expiresIn * 1000).toString());
    }
    this.scheduleRefresh(expiresIn);
  }

  clearTokens(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(this.sessionKey);
      sessionStorage.removeItem(this.tokenExpiresKey);
    }
    if (typeof localStorage !== 'undefined') {
      // Legacy keys from prior auth implementations
      localStorage.removeItem(this.sessionKey);
      localStorage.removeItem(this.tokenExpiresKey);
      localStorage.removeItem('auth_remember_me');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
    this.stopActivityCheck();
    this.clearUserStoreSession();
  }

  private scheduleRefresh(expiresInSeconds: number): void {
    try {
      if (this.refreshTimerId) {
        clearTimeout(this.refreshTimerId);
      }
      const refreshDelayMs = Math.max(expiresInSeconds * 1000 - 5 * 60 * 1000, 30 * 1000);
      this.refreshTimerId = window.setTimeout(async () => {
        try {
          await this.refreshToken();
        } catch {
          // next request will handle re-auth
        }
      }, refreshDelayMs);
    } catch {
      // non-browser env
    }
  }

  private initializeActivityDetection(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    if (this.activityListenersAttached) return;

    const handleVisibilityChange = () => {
      this.isPageVisible = !document.hidden;
      if (this.isPageVisible && this.hasSession()) {
        this.startActivityCheck();
        this.checkAndRefreshTokenIfNeeded();
        void this.syncLiveUserToStore();
      } else if (!this.isPageVisible) {
        this.stopActivityCheck();
      }
    };

    const handleActivity = () => {
      this.lastActivityTime = Date.now();
      if (this.isPageVisible && !this.activityCheckIntervalId && this.hasSession()) {
        this.startActivityCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousemove', handleActivity, { passive: true });
    document.addEventListener('keydown', handleActivity, { passive: true });
    document.addEventListener('touchstart', handleActivity, { passive: true });
    document.addEventListener('click', handleActivity, { passive: true });
    document.addEventListener('scroll', handleActivity, { passive: true });

    window.addEventListener('focus', () => {
      this.isPageVisible = true;
      if (this.hasSession()) {
        this.startActivityCheck();
        this.checkAndRefreshTokenIfNeeded();
        void this.syncLiveUserToStore();
      }
    });

    this.activityListenersAttached = true;

    if (this.isPageVisible && this.hasSession()) {
      this.startActivityCheck();
    }
  }

  private startActivityCheck(): void {
    if (this.activityCheckIntervalId || !this.hasSession()) {
      return;
    }

    this.activityCheckIntervalId = window.setInterval(() => {
      const timeSinceActivity = Date.now() - this.lastActivityTime;
      const isUserActive = timeSinceActivity < 5 * 60 * 1000;

      if (this.isPageVisible && isUserActive && this.hasSession()) {
        this.checkAndRefreshTokenIfNeeded();
      } else if (!isUserActive) {
        this.stopActivityCheck();
      }
    }, 2 * 60 * 1000);
  }

  private stopActivityCheck(): void {
    if (this.activityCheckIntervalId) {
      clearInterval(this.activityCheckIntervalId);
      this.activityCheckIntervalId = null;
    }
  }

  private async checkAndRefreshTokenIfNeeded(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    const expires = sessionStorage.getItem(this.tokenExpiresKey);
    if (!expires) return;

    const timeUntilExpiry = parseInt(expires, 10) - Date.now();
    const refreshThreshold = 10 * 60 * 1000;

    if (timeUntilExpiry < refreshThreshold && timeUntilExpiry > 0) {
      try {
        await this.refreshToken();
      } catch {
        // silently fail
      }
    }
  }

  async refreshToken(): Promise<boolean> {
    if (this.isRefreshing) {
      return this.refreshPromise!;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._performRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async _performRefresh(): Promise<boolean> {
    let response: Response;
    try {
      await ensureCsrfCookie();
      const csrfToken = getCookie('csrf_token');
      response = await fetch(apiEndpoints.auth.refresh, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
    } catch (error) {
      throw new TransientTokenRefreshError(
        `Network error during token refresh: ${(error as Error).message}`,
      );
    }

    let responseText = '';
    try {
      responseText = await response.text();
    } catch (e) {
      throw new TransientTokenRefreshError(
        `Failed to read refresh response: ${(e as Error).message}`,
      );
    }

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error(
          `Token refresh failed: ${response.status} ${response.statusText}${responseText ? ` - ${responseText}` : ''}`,
        );
      }
      throw new TransientTokenRefreshError(
        `Token refresh HTTP ${response.status}: ${responseText ? responseText.substring(0, 200) : response.statusText}`,
      );
    }

    if (!responseText || !responseText.trim()) {
      throw new TransientTokenRefreshError('Empty response from refresh endpoint');
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new TransientTokenRefreshError(
        `Invalid refresh response format: ${responseText.substring(0, 100)}`,
      );
    }

    let data: RefreshResponse;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new TransientTokenRefreshError(
        `Failed to parse refresh JSON: ${(e as Error).message}`,
      );
    }

    if (data.success && data.data?.expiresIn) {
      this.storeSessionMetadata(data.data.expiresIn);
      if (data.data.user) {
        await this.applyLiveUserToStore(data.data.user);
      } else {
        await this.syncLiveUserToStore(true);
      }
      if (this.isPageVisible) {
        this.startActivityCheck();
      }
      return true;
    }

    throw new Error(data.message || 'Token refresh failed');
  }

  async getValidToken(): Promise<string | null> {
    if (!this.hasSession()) {
      return null;
    }
    if (this.isTokenExpired() || this.isTokenNearExpiry()) {
      try {
        await this.refreshToken();
      } catch {
        return null;
      }
    }
    return 'cookie-session';
  }

  async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {},
    requestOptions: AuthRequestOptions = {}
  ): Promise<Response> {
    const { log, debug: logDebug, error: logErrorFn } = await import('./console');

    let fullUrl = url;
    if (!url.startsWith('http')) {
      fullUrl = window.location.origin + (url.startsWith('/') ? url : '/' + url);
    }

    const quietLog = !isLoggingEndpointUrl(fullUrl) && !isStreamServiceUrl(fullUrl);
    if (quietLog) {
      log(`[authManager] makeAuthenticatedRequest: ${options.method || 'GET'} ${fullUrl}`);
    }

    if (!this.hasSession()) {
      const { isLoggedIn } = await import('../store/userStore');
      const userIsNotAuthenticated = !isLoggedIn();

      if (userIsNotAuthenticated) {
        logDebug(`[authManager] No active session for ${fullUrl} (user not authenticated)`);
        return new Response(JSON.stringify({ success: false, message: 'No valid session available' }), {
          status: 401,
          statusText: 'Unauthorized',
          headers: { 'Content-Type': 'application/json' },
        });
      }

      logDebug(
        `[authManager] Session metadata missing for ${fullUrl} but user is logged in — attempting cookie request`
      );
    }

    if (this.isTokenExpired() || this.isTokenNearExpiry()) {
      try {
        await this.refreshToken();
      } catch (error) {
        if (!isTransientTokenRefreshError(error)) {
          logDebug(
            `[authManager] Token refresh failed before ${fullUrl}; continuing with existing cookies`
          );
        }
      }
    }

    const authOptions: RequestInit = {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
      },
    };

    const method = (authOptions.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      const csrfToken = getCookie('csrf_token');
      if (csrfToken) {
        (authOptions.headers as Record<string, string>)['X-CSRF-Token'] = csrfToken;
      }
    }

    const MAX_RETRIES = 2;
    const INITIAL_RETRY_DELAY = 500;
    const MAX_RETRY_DELAY = 2000;

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (quietLog) {
          if (attempt > 0) {
            log(`[authManager] Retry attempt ${attempt}/${MAX_RETRIES} for ${fullUrl}`);
          } else {
            log(`[authManager] Calling fetch for ${fullUrl} with method ${method}`);
          }
        }

        const response = await fetch(fullUrl, authOptions);
        if (quietLog) {
          log(`[authManager] Fetch response: ${response.status} ${response.statusText} for ${fullUrl}`);
        }

        if (response.status === 401) {
          if (isStreamServiceUrl(fullUrl)) {
            return response;
          }
          if (quietLog) {
            log(`[authManager] Got 401, attempting token refresh for ${fullUrl}`);
          }
          try {
            await this.refreshToken();
            const retryResponse = await fetch(fullUrl, authOptions);
            if (quietLog) {
              log(`[authManager] Retry response: ${retryResponse.status} ${retryResponse.statusText} for ${fullUrl}`);
            }
            return retryResponse;
          } catch (error) {
            if (isTransientTokenRefreshError(error)) {
              if (quietLog) {
                log(`[authManager] Token refresh transient failure for ${fullUrl}; session kept`);
              }
            } else {
              logErrorFn(`[authManager] Token refresh failed for ${fullUrl}:`, error);
            }
          }
        }

        return response;
      } catch (fetchError) {
        lastError = fetchError as Error;

        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw fetchError;
        }

        const isStreamingEndpoint = fullUrl.includes('/api/stream/');
        const errorMessage = (fetchError as Error).message || String(fetchError);
        const isFailedToFetch = errorMessage.includes('Failed to fetch');

        if (isStreamingEndpoint && isFailedToFetch) {
          throw fetchError;
        }

        const isNetworkError =
          isFailedToFetch ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('network') ||
          errorMessage.includes('timeout');

        if (isNetworkError && attempt < MAX_RETRIES) {
          const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, attempt), MAX_RETRY_DELAY);
          if (quietLog) {
            log(`[authManager] Network error on attempt ${attempt + 1}/${MAX_RETRIES + 1}, retrying in ${delay}ms: ${errorMessage}`);
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // A best-effort caller has already decided this failure is survivable, so it is a trace, not an
        // error. The throw still happens — only the log level changes.
        const logFailure = requestOptions.bestEffort && isNetworkError ? logDebug : logErrorFn;
        logFailure(`[authManager] Fetch error for ${fullUrl} (after ${attempt + 1} attempt(s)):`, {
          error: fetchError,
          context: { url: fullUrl, method, errorMessage },
        });

        throw fetchError;
      }
    }

    throw lastError || new Error('Unknown error in makeAuthenticatedRequest');
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      await ensureCsrfCookie();

      const csrfToken = getCookie('csrf_token');
      const response = await fetch(apiEndpoints.auth.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      let responseText = '';
      try {
        responseText = await response.text();
      } catch (e) {
        return {
          success: false,
          message: `Failed to read response: ${(e as Error).message}`,
        };
      }

      if (!response.ok) {
        const message =
          response.status === 401
            ? 'Invalid email or password'
            : response.status === 429
              ? 'Too many attempts. Please try again later.'
              : `Login failed: ${response.status} ${response.statusText}${responseText ? ` - ${responseText}` : ''}`;
        return { success: false, message };
      }

      if (!responseText || !responseText.trim()) {
        return { success: false, message: 'Empty response from server' };
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return { success: false, message: `Invalid response format: ${responseText}` };
      }

      let data: LoginResponse;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        return {
          success: false,
          message: `Failed to parse JSON response: ${(e as Error).message}. Response: ${responseText.substring(0, 100)}`,
        };
      }

      if (data.success && data.data) {
        this.storeSessionMetadata(data.data.expiresIn);

        try {
          const healthCalls = [
            `${config.API_BASE_URL}/health`,
            `${config.ADMIN_BASE_URL}/health`,
            `${config.FILE_BASE_URL}/health`,
            `${config.MEDIA_BASE_URL}/health`,
          ];
          await Promise.all(healthCalls.map((u) => fetch(u, { method: 'GET', credentials: 'include' })));
        } catch {
          /* ignore */
        }

        if (this.isPageVisible) {
          this.startActivityCheck();
        }

        return {
          success: true,
          data: data.data,
        };
      }

      return {
        success: false,
        message: data.message || 'Login failed',
      };
    } catch (error) {
      return {
        success: false,
        message: `Network error: ${(error as Error).message}`,
      };
    }
  }

  async register(userData: UserData): Promise<RegisterResponse> {
    try {
      await ensureCsrfCookie();

      const csrfToken = getCookie('csrf_token');
      const response = await fetch(apiEndpoints.auth.register, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(userData),
      });

      let responseText = '';
      try {
        responseText = await response.text();
      } catch (e) {
        return {
          success: false,
          message: `Failed to read response: ${(e as Error).message}`,
        };
      }

      if (!response.ok) {
        return {
          success: false,
          message: `Registration failed: ${response.status} ${response.statusText}${responseText ? ` - ${responseText}` : ''}`,
        };
      }

      if (!responseText || !responseText.trim()) {
        return { success: false, message: 'Empty response from server' };
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return { success: false, message: `Invalid response format: ${responseText}` };
      }

      try {
        return JSON.parse(responseText);
      } catch (e) {
        return {
          success: false,
          message: `Failed to parse JSON response: ${(e as Error).message}. Response: ${responseText.substring(0, 100)}`,
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `Network error: ${(error as Error).message}`,
      };
    }
  }

  async logout(): Promise<void> {
    try {
      if (!getCookie('csrf_token')) {
        await ensureCsrfCookie();
      }
      const csrfToken = getCookie('csrf_token');
      await fetch(apiEndpoints.auth.logout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
    } catch (error) {
      logError('Logout request failed:', error);
    }

    this.clearTokens();
  }

  async getCurrentUser(): Promise<UserResponse> {
    try {
      const response = await this.makeAuthenticatedRequest(apiEndpoints.auth.me);

      if (response.ok) {
        const data: UserResponse = await response.json();
        return data;
      }

      return {
        success: false,
        message: `Authentication failed: ${response.status} ${response.statusText}`,
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to get user info: ${(error as Error).message}`,
      };
    }
  }

  /**
   * Restore session on app startup using HttpOnly auth cookies.
   */
  async restoreSessionFromCookies(): Promise<UserResponse | null> {
    if (this.hasSession()) {
      const existing = await this.fetchCurrentUserWithCookies();
      if (existing.success) {
        return existing;
      }
    }

    const refreshed = await this.tryRefreshFromCookies();
    if (!refreshed) {
      return null;
    }

    return this.fetchCurrentUserWithCookies();
  }

  private async fetchCurrentUserWithCookies(): Promise<UserResponse> {
    try {
      await ensureCsrfCookie();
      const csrfToken = getCookie('csrf_token');
      const response = await fetch(apiEndpoints.auth.me, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });

      if (response.ok) {
        return (await response.json()) as UserResponse;
      }

      return {
        success: false,
        message: `Authentication failed: ${response.status} ${response.statusText}`,
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to get user info: ${(error as Error).message}`,
      };
    }
  }

  private async tryRefreshFromCookies(): Promise<boolean> {
    try {
      await ensureCsrfCookie();
      const csrfToken = getCookie('csrf_token');
      const response = await fetch(apiEndpoints.auth.refresh, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        return false;
      }

      const data: RefreshResponse = await response.json();
      if (data.success && data.data?.expiresIn) {
        this.storeSessionMetadata(data.data.expiresIn);
        if (this.isPageVisible) {
          this.startActivityCheck();
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  isAuthenticated(): boolean {
    return this.hasSession() && !this.isTokenExpired();
  }
}

export const authManager = new AuthManager();
export default authManager;
